import React, { useEffect, useState } from 'react';
import api from '../api';
import { useToast } from '../context/ToastContext';
import { DollarSign, TrendingUp, TrendingDown, BarChart3, Repeat, AlertTriangle, Users } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
// formatCurrency imported for future use — currently inline formatting in AdminFinance
// import { formatCurrency } from '../utils';

export default function AdminFinance() {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/api/admin/finance?days=${days}`)
      .then(({ data: d }) => setData(d))
      .catch((err) => { console.error(err); toast.error('Kunne ikke laste økonomioversikt'); })
      .finally(() => setLoading(false));
  }, [days]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading || !data) return (
    <div className="space-y-6 animate-pulse">
      <div className="h-8 bg-navy-800 rounded w-48" />
      <div className="grid grid-cols-4 gap-4">{[1,2,3,4].map(i => <div key={i} className="h-28 bg-navy-800 rounded-lg" />)}</div>
      <div className="h-80 bg-navy-800 rounded-lg" />
    </div>
  );

  const tooltipStyle = { backgroundColor: '#121927', border: '1px solid #222F43', borderRadius: '8px', color: '#F8FAFC' };
  const chartData = data.company_finance.map(c => ({ name: c.name.length > 15 ? c.name.substring(0,15)+'…' : c.name, fullName: c.name, revenue: c.monthly_revenue, cost: c.total_cost, profit: c.margin_amount }));

  return (
    <div data-testid="admin-finance-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white tracking-tight">Økonomi</h1>
          <p className="text-slate-500 text-sm mt-1">Inntekter, kostnader og margin per klient</p>
        </div>
        <div className="flex gap-2">
          {[30, 60, 90].map(d => (
            <button key={d} onClick={() => setDays(d)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${days === d ? 'bg-accent-blue text-white' : 'bg-navy-800 text-slate-400 border border-navy-600 hover:border-accent-blue'}`}>
              {d} dager
            </button>
          ))}
        </div>
      </div>

      {/* MRR / ARR / ARPU / At-risk summary row */}
      {(() => {
        const companies = data.company_finance || [];
        const mrr = data.total_revenue || 0;
        const arr = mrr * 12;
        const activeCount = companies.length;
        const arpu = activeCount > 0 ? Math.round(mrr / activeCount) : 0;
        const atRiskCount = companies.filter(c => (c.payment_status === 'past_due') || (c.days_inactive && c.days_inactive > 14) || ((c.call_count || 0) === 0)).length;
        return (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="finance-mrr-row">
            <div className="bg-gradient-to-br from-accent-blue/10 to-transparent border border-accent-blue/30 rounded-lg p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-9 h-9 bg-accent-blue/25 rounded-lg flex items-center justify-center"><Repeat size={16} className="text-accent-blue" /></div>
                <span className="text-xs font-semibold tracking-widest uppercase text-accent-blue">MRR</span>
              </div>
              <div className="text-2xl font-heading font-bold text-white">{mrr.toLocaleString('nb-NO')} kr</div>
              <div className="text-xs text-slate-500 mt-1">månedlig gjentakende inntekt</div>
            </div>
            <div className="bg-navy-800 border border-navy-600 rounded-lg p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-9 h-9 bg-accent-green/20 rounded-lg flex items-center justify-center"><TrendingUp size={16} className="text-accent-green" /></div>
                <span className="text-xs font-semibold tracking-widest uppercase text-slate-500">ARR</span>
              </div>
              <div className="text-2xl font-heading font-bold text-white">{arr.toLocaleString('nb-NO')} kr</div>
              <div className="text-xs text-slate-500 mt-1">projisert (MRR × 12)</div>
            </div>
            <div className="bg-navy-800 border border-navy-600 rounded-lg p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-9 h-9 bg-purple-500/20 rounded-lg flex items-center justify-center"><Users size={16} className="text-purple-400" /></div>
                <span className="text-xs font-semibold tracking-widest uppercase text-slate-500">ARPU</span>
              </div>
              <div className="text-2xl font-heading font-bold text-white">{arpu.toLocaleString('nb-NO')} kr</div>
              <div className="text-xs text-slate-500 mt-1">snitt inntekt per klient ({activeCount})</div>
            </div>
            <div className={`rounded-lg p-5 border ${atRiskCount > 0 ? 'bg-accent-red/10 border-accent-red/30' : 'bg-navy-800 border-navy-600'}`}>
              <div className="flex items-center gap-2 mb-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${atRiskCount > 0 ? 'bg-accent-red/25' : 'bg-navy-700'}`}>
                  <AlertTriangle size={16} className={atRiskCount > 0 ? 'text-accent-red' : 'text-slate-400'} />
                </div>
                <span className="text-xs font-semibold tracking-widest uppercase text-slate-500">Klienter i risiko</span>
              </div>
              <div className={`text-2xl font-heading font-bold ${atRiskCount > 0 ? 'text-accent-red' : 'text-white'}`}>{atRiskCount}</div>
              <div className="text-xs text-slate-500 mt-1">inaktiv &gt;14d eller forfalt betaling</div>
            </div>
          </div>
        );
      })()}

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-9 h-9 bg-accent-green/20 rounded-lg flex items-center justify-center"><TrendingUp size={16} className="text-accent-green" /></div>
            <span className="text-xs font-semibold tracking-widest uppercase text-slate-500">Total inntekt</span>
          </div>
          <div className="text-2xl font-heading font-bold text-white">{(data.total_revenue || 0).toLocaleString('nb-NO')} kr</div>
          <div className="text-xs text-slate-500 mt-1">per måned</div>
        </div>
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-9 h-9 bg-accent-red/20 rounded-lg flex items-center justify-center"><TrendingDown size={16} className="text-accent-red" /></div>
            <span className="text-xs font-semibold tracking-widest uppercase text-slate-500">Total kostnad</span>
          </div>
          <div className="text-2xl font-heading font-bold text-white">{(data.total_cost || 0).toLocaleString('nb-NO')} kr</div>
          <div className="text-xs text-slate-500 mt-1">siste {days} dager</div>
        </div>
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-9 h-9 bg-accent-blue/20 rounded-lg flex items-center justify-center"><DollarSign size={16} className="text-accent-blue" /></div>
            <span className="text-xs font-semibold tracking-widest uppercase text-slate-500">Total fortjeneste</span>
          </div>
          <div className="text-2xl font-heading font-bold text-accent-green">{(data.total_profit || 0).toLocaleString('nb-NO')} kr</div>
        </div>
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-9 h-9 bg-purple-500/20 rounded-lg flex items-center justify-center"><BarChart3 size={16} className="text-purple-400" /></div>
            <span className="text-xs font-semibold tracking-widest uppercase text-slate-500">Total margin</span>
          </div>
          <div className="text-2xl font-heading font-bold text-white">{data.total_margin_pct}%</div>
        </div>
      </div>

      {/* Revenue vs Cost chart */}
      <div className="bg-navy-800 border border-navy-600 rounded-lg p-6 mb-8">
        <h3 className="font-heading text-base font-semibold text-white mb-4">Inntekt vs. kostnad per bedrift</h3>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} barGap={4}>
              <XAxis dataKey="name" tick={{ fill: '#64748B', fontSize: 10 }} axisLine={{ stroke: '#222F43' }} tickLine={false} />
              <YAxis tick={{ fill: '#64748B', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `${v} kr`} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [`${v.toLocaleString('nb-NO')} kr`, n]} />
              <Bar dataKey="revenue" fill="#34D399" radius={[4,4,0,0]} name="Inntekt" />
              <Bar dataKey="cost" fill="#F87171" radius={[4,4,0,0]} name="Kostnad" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Per-company table */}
      <div className="bg-navy-800 border border-navy-600 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-navy-600">
          <h3 className="font-heading text-base font-semibold text-white">Detaljert oversikt per bedrift</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="finance-table">
            <thead>
              <tr className="border-b border-navy-600">
                <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Bedrift</th>
                <th className="text-right px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Samtaler</th>
                <th className="text-right px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Inntekt</th>
                <th className="text-right px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Kostnad</th>
                <th className="text-right px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Snitt/samtale</th>
                <th className="text-right px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Fortjeneste</th>
                <th className="text-right px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Margin</th>
              </tr>
            </thead>
            <tbody>
              {data.company_finance.map(c => (
                <tr key={c._id} className="border-b border-navy-600/50 hover:bg-navy-700/50 transition-colors">
                  <td className="px-4 py-3.5 text-sm font-medium text-white">{c.name}</td>
                  <td className="px-4 py-3.5 text-sm text-slate-400 text-right font-mono">{c.call_count}</td>
                  <td className="px-4 py-3.5 text-sm text-white text-right font-mono">{(c.monthly_revenue || 0).toLocaleString('nb-NO')} kr</td>
                  <td className="px-4 py-3.5 text-sm text-accent-red text-right font-mono">{(c.total_cost || 0).toLocaleString('nb-NO')} kr</td>
                  <td className="px-4 py-3.5 text-sm text-slate-400 text-right font-mono">{c.avg_cost_per_call} kr</td>
                  <td className="px-4 py-3.5 text-sm text-accent-green text-right font-mono">{(c.margin_amount || 0).toLocaleString('nb-NO')} kr</td>
                  <td className="px-4 py-3.5 text-right">
                    <span className={`text-sm font-mono font-medium ${c.margin_pct >= 70 ? 'text-accent-green' : c.margin_pct >= 40 ? 'text-accent-yellow' : 'text-accent-red'}`}>
                      {c.margin_pct}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Cost breakdown */}
        <div className="px-6 py-4 border-t border-navy-600">
          <h4 className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-3">Kostnadsfordeling</h4>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {['voice', 'tts', 'llm', 'telephony', 'other'].map(key => {
              const total = data.company_finance.reduce((s, c) => s + (c.cost_breakdown?.[key] || 0), 0);
              const labels = { voice: 'Stemme', tts: 'TTS', llm: 'LLM', telephony: 'Telefoni', other: 'Annet' };
              return (
                <div key={key} className="bg-navy-900 rounded-lg p-3 text-center">
                  <div className="text-sm font-heading font-bold text-white">{total.toFixed(0)} kr</div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">{labels[key]}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
